if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('[data-product-gallery-dots]'),
        };
        this.mql = window.matchMedia('(min-width: 990px)');
        this.mediaList = this.elements.viewer.slider || this.elements.viewer.querySelector('.product__media-list');
        this.onMediaScroll = this.scheduleActiveMediaSync.bind(this);

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.mediaList?.addEventListener('scroll', this.onMediaScroll, { passive: true });
        this.setupGalleryDots();
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
        this.setupDesktopObserver();
      }

      disconnectedCallback() {
        this.mediaObserver?.disconnect();
        this.mediaList?.removeEventListener('scroll', this.onMediaScroll);
        if (this.gallerySyncFrame) window.cancelAnimationFrame(this.gallerySyncFrame);
      }

      setupGalleryDots() {
        if (!this.elements.dots) return;

        const mediaItems = this.getGalleryMediaItems();
        this.elements.dots.replaceChildren();
        mediaItems.forEach((mediaItem, index) => {
          const dot = document.createElement('button');
          const mediaLabel = this.elements.thumbnails
            ?.querySelector(`[data-target="${mediaItem.dataset.mediaId}"] button`)
            ?.getAttribute('aria-label');
          dot.type = 'button';
          dot.className = 'product-gallery__dot';
          dot.dataset.target = mediaItem.dataset.mediaId;
          dot.setAttribute('aria-label', mediaLabel || `${index + 1}`);
          dot.addEventListener('click', this.setActiveMedia.bind(this, mediaItem.dataset.mediaId, false));
          this.elements.dots.append(dot);
        });

        if (this.elements.viewer.pageTotalElement) {
          this.elements.viewer.pageTotalElement.textContent = mediaItems.length;
        }
        this.setActiveDot(mediaItems[0]?.dataset.mediaId);
      }

      getGalleryMediaItems() {
        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]')
        );
        const visibleMediaItems = mediaItems.filter((mediaItem) => mediaItem.clientWidth > 0);

        return visibleMediaItems.length ? visibleMediaItems : mediaItems;
      }

      setupDesktopObserver() {
        if (!this.classList.contains('product-gallery--cuyana') || !window.IntersectionObserver) return;

        this.mediaObserver = new IntersectionObserver(
          (entries) => {
            if (!this.mql.matches) return;
            const visibleEntry = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
            if (!visibleEntry) return;

            this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
              item.classList.toggle('is-active', item === visibleEntry.target);
            });
            const mediaId = visibleEntry.target.dataset.mediaId;
            const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
            this.setActiveThumbnail(thumbnail);
            this.setActiveDot(mediaId);
          },
          { rootMargin: '-15% 0px -55% 0px', threshold: [0.25, 0.5, 0.75] }
        );

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => this.mediaObserver.observe(item));
      }

      onSlideChanged(event) {
        if (!this.mql.matches) {
          this.syncActiveMediaFromScroll();
          return;
        }

        this.setActiveDot(event.detail.currentElement.dataset.mediaId);
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      scheduleActiveMediaSync() {
        if (this.mql.matches || this.gallerySyncFrame) return;

        this.gallerySyncFrame = window.requestAnimationFrame(() => {
          this.gallerySyncFrame = null;
          this.syncActiveMediaFromScroll();
        });
      }

      syncActiveMediaFromScroll() {
        if (this.mql?.matches) return;

        const slider = this.mediaList || this.elements.viewer.slider;
        const mediaItems = this.getGalleryMediaItems();
        if (!slider || !mediaItems.length) return;

        const sliderRect = slider.getBoundingClientRect();
        let activeMedia = mediaItems[0];
        let greatestVisibleWidth = 0;

        mediaItems.forEach((mediaItem) => {
          const mediaRect = mediaItem.getBoundingClientRect();
          const visibleWidth = Math.max(
            0,
            Math.min(mediaRect.right, sliderRect.right) - Math.max(mediaRect.left, sliderRect.left)
          );
          if (visibleWidth > greatestVisibleWidth) {
            greatestVisibleWidth = visibleWidth;
            activeMedia = mediaItem;
          }
        });

        const activePosition = mediaItems.indexOf(activeMedia) + 1;
        this.setActiveDot(activeMedia.dataset.mediaId);
        if (this.elements.viewer.currentPageElement) {
          this.elements.viewer.currentPageElement.textContent = activePosition;
        }
      }

      resetToFirstMedia(options = {}) {
        const firstMedia = this.elements.viewer.querySelector('[data-media-id]');
        if (!firstMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((mediaItem) => {
          mediaItem.classList.toggle('is-active', mediaItem === firstMedia);
        });
        const mediaList = this.elements.viewer.slider || this.elements.viewer.querySelector('.product__media-list');
        mediaList?.scrollTo({ left: 0, behavior: 'auto' });

        const firstThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${firstMedia.dataset.mediaId}"]`);
        this.setActiveThumbnail(firstThumbnail);
        this.setActiveDot(firstMedia.dataset.mediaId);

        if (options.scrollOnDesktop && this.mql.matches) {
          this.preventStickyHeader();
          firstMedia.scrollIntoView({ block: 'start' });
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (this.classList.contains('product-gallery--cuyana') && this.mql.matches && !prepend) {
          this.preventStickyHeader();
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'start' });
          this.playActiveMedia(activeMedia);
          const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.setActiveDot(mediaId);
          return;
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.setActiveDot(mediaId);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition || activeMedia.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.classList.contains('product-gallery--cuyana') && this.mql.matches) {
          const visibleTop = this.elements.thumbnails.scrollTop;
          const visibleBottom = visibleTop + this.elements.thumbnails.clientHeight;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          if (thumbnailTop < visibleTop || thumbnailBottom > visibleBottom) {
            this.elements.thumbnails.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          }
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveDot(mediaId) {
        if (!this.elements.dots || !mediaId) return;
        this.elements.dots.querySelectorAll('button').forEach((dot) => {
          if (dot.dataset.target === mediaId) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
